import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Grid,
  useTheme
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import CreateIcon from '@mui/icons-material/Create';
import SignatureCanvas from 'react-signature-canvas';
import { trackingAPI, signaturesAPI } from '../../services/api';
import './PublicProposal.css';

// Print-specific styles
const printStyles = `
  @media print {
    @page {
      size: A4;
      margin: 0;
    }
    body {
      margin: 0;
      padding: 0;
    }
    .MuiContainer-root {
      padding: 0 !important;
      max-width: 100% !important;
    }
    .MuiPaper-root {
      box-shadow: none !important;
      width: 21cm !important;
      min-height: 29.7cm !important;
      padding: 1cm !important;
      margin: 0 !important;
    }
    .no-print {
      display: none !important;
    }
    .proposal-header {
      margin-bottom: 1.5cm !important;
    }
    .proposal-content {
      margin-bottom: 1.5cm !important;
    }
    .signature-section {
      margin-bottom: 1cm !important;
    }
    .footer-section {
      margin-top: 1cm !important;
    }
    .no-page-break {
      page-break-inside: avoid;
    }
  }
`;

const PublicProposal = () => {
  const theme = useTheme();
  const { uuid } = useParams();
  const [proposal, setProposal] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptingProposal, setAcceptingProposal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const abortControllerRef = useRef(null);
  
  // Brand colors
  const brandColors = {
    primary: '#454546', // Dark Gray (changed from orange to match title color)
    secondary: '#454546', // Dark Gray
    accent: '#e0e0e0', // Light Gray (changed from #f8f8f8 to a more visible light gray)
    light: '#ffffff',
    dark: '#333333'
  };

  // Helper function to construct logo URL
  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('http')) return logoPath;
    // Ensure the path starts with /uploads for static file serving
    if (logoPath.startsWith('/uploads/')) {
      return logoPath;
    }
    // If path doesn't start with /uploads/, prepend it
    return `/uploads/${logoPath.replace(/^\//, '')}`;
  };

  // Add handlePrint function
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  /**
   * Handle PDF download using server-side generation
   * This function generates the PDF on the server and downloads it to the client
   */
  const handleDownloadPDF = async () => {
    try {
      setPdfLoading(true);
      setSnackbar({ open: true, message: 'Generating PDF... This may take a few seconds.', severity: 'info' });

      // Step 1: Generate PDF on the server
      const generateResponse = await fetch(`/api/proposals/${proposal.id}/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!generateResponse.ok) {
        const errorData = await generateResponse.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to generate PDF');
      }

      const generateData = await generateResponse.json();
      
      if (!generateData.success) {
        throw new Error(generateData.message || 'Failed to generate PDF');
      }

      setSnackbar({ open: true, message: 'PDF generated successfully. Downloading...', severity: 'info' });

      // Step 2: Download the PDF file
      const downloadResponse = await fetch(`/api/proposals/${proposal.id}/pdf/download`, {
        method: 'GET'
      });
      
      if (!downloadResponse.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create a blob from the response
      const blob = await downloadResponse.blob();
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proposal-${proposal.proposalNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: 'PDF downloaded successfully', severity: 'success' });
    } catch (error) {
      console.error('Error generating/downloading PDF:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to generate PDF';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 401:
            errorMessage = 'Session expired. Please refresh the page.';
            break;
          case 403:
            errorMessage = 'You do not have permission to download this PDF.';
            break;
          case 404:
            errorMessage = 'Proposal not found.';
            break;
          case 500:
            errorMessage = data?.message || 'Server error occurred while generating PDF.';
            break;
          default:
            errorMessage = data?.message || `Error ${status}: Failed to generate PDF.`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = error.message || 'Failed to generate PDF';
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  // Function to fetch proposal data - extracted for reuse
  const fetchProposalData = async (signal) => {
    try {
      setLoading(true);
      console.log('Fetching proposal with UUID:', uuid);
      const response = await fetch(`/api/tracking/proposal/${uuid}`, {
        signal: signal
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}: Proposal not found`);
      }
      
      const data = await response.json();
      console.log('Proposal data received:', data);
      console.log('Customer signature from API:', data.proposal?.customerSignature);
      console.log('Customer signature exists:', !!data.proposal?.customerSignature);
      console.log('Company settings received:', data.companySettings);
      
      setProposal(data.proposal);
      setCompanySettings(data.companySettings);
      return data;
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Fetch aborted');
        return null;
      }
      console.error('Error fetching proposal:', err);
      setError(err.message);
      return null;
    } finally {
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  };
  
  useEffect(() => {
    const abortController = new AbortController();

    if (uuid) {
      fetchProposalData(abortController.signal);
    }
    
    return () => {
      abortController.abort();
      // Also cancel any ongoing requests from event handlers
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [uuid]);

  // Helper function to get status chip color using brand colors
  const getStatusChipColor = (status) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return '#9e9e9e'; // Gray
      case 'sent':
        return '#2196f3'; // Blue
      case 'viewed':
        return brandColors.primary; // Orange
      case 'accepted':
        return '#4caf50'; // Green
      case 'declined':
        return '#f44336'; // Red
      case 'expired':
        return '#ff9800'; // Amber
      default:
        return '#9e9e9e'; // Gray
    }
  };
  
  // Helper function to get status color (for backward compatibility)
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'viewed':
        return 'primary';
      case 'accepted':
        return 'success';
      case 'declined':
        return 'error';
      case 'expired':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleCreateSignature = () => {
    if (!signerName.trim()) {
      setSnackbar({ open: true, message: 'Please enter your name', severity: 'error' });
      return;
    }
    // Create a signature object with the current timestamp
    const newSignature = {
      signerName: signerName,
      signature: signerName,
      signedAt: new Date().toISOString()
    };
    
    console.log('Generated new signature:', newSignature);
    setSignatureData(newSignature);
  };

  const handleSignProposal = async (signaturePayload = null) => {
    if (!signaturePayload && !signatureData) {
      setSnackbar({ open: true, message: 'Please create your signature first', severity: 'error' });
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setSignatureLoading(true);
      
      // Use provided signature payload or use the signatureData state
      const payload = signaturePayload || signatureData;
      
      console.log('Sending signature payload:', payload);

      const response = await fetch(`/api/tracking/proposal/${uuid}/customer-sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal
      });
      
      console.log('Signature API response status:', response.status);
      console.log('Signature API response headers:', response.headers);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to sign proposal');
      }

      const updatedProposal = await response.json();
      console.log('Signature response:', updatedProposal);
      
      // Make sure we're updating the proposal state with the latest data
      if (updatedProposal && updatedProposal.proposal) {
        setProposal(updatedProposal.proposal);
        console.log('Updated proposal state with customer signature:', updatedProposal.proposal.customerSignature);
      } else if (updatedProposal) {
        // If the API doesn't return the full proposal, fetch it again
        await fetchProposalData();
      }
      
      setSignatureDialogOpen(false);
      setSnackbar({ open: true, message: 'Proposal signed successfully!', severity: 'success' });
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Sign request aborted');
        return;
      }
      console.error('Error signing proposal:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setSignatureLoading(false);
      }
    }
  };

  const generateSignature = () => {
    if (!signerName.trim()) {
      setSnackbar({ open: true, message: 'Please enter your name', severity: 'error' });
      return;
    }
    
    // Create a signature object with the current timestamp
    const newSignature = {
      signerName: signerName,
      signature: signerName,
      signedAt: new Date().toISOString()
    };
    
    console.log('Generated new signature:', newSignature);
    setSignatureData(newSignature);
    
    // No longer automatically save the signature to the server
    // The user must explicitly click the Accept Proposal button
    setSnackbar({ open: true, message: 'Signature generated. Click "Accept Proposal" to complete the process.', severity: 'info' });
  };

  const handleAcceptProposal = async () => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setAcceptLoading(true);
      setAcceptingProposal(true);
      
      // Check if we have local signature data that hasn't been submitted to the server yet
      if (signatureData) {
        console.log('Local signature data found, submitting to server first...');
        // Submit the signature to the server first
        await handleSignProposal(signatureData);
      }
      
      // Now fetch the latest proposal data to ensure we have the most up-to-date signature
      console.log('Fetching latest proposal data before accepting...');
      const latestData = await fetchProposalData(abortControllerRef.current.signal);
      
      if (!latestData || !latestData.proposal || !latestData.proposal.customerSignature) {
        console.error('No customer signature found in latest proposal data');
        throw new Error('Please sign the proposal before accepting');
      }
      
      console.log('Latest customer signature:', latestData.proposal.customerSignature);
      
      // Parse the signature data safely
      let serverSignatureData;
      try {
        serverSignatureData = typeof latestData.proposal.customerSignature === 'string' ? 
          JSON.parse(latestData.proposal.customerSignature) : latestData.proposal.customerSignature;
      } catch (error) {
        console.error('Error parsing signature data:', error);
        throw new Error('Invalid signature data. Please sign the proposal again.');
      }
      
      // Send the request to accept the proposal
      console.log('Sending accept request with signature data:', serverSignatureData);
      const response = await fetch(`/api/tracking/proposal/${uuid}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // Include the signature data in the request body
        body: JSON.stringify({
          signature: serverSignatureData.signature,
          signerName: serverSignatureData.signerName,
          signedAt: serverSignatureData.signedAt
        }),
        signal: abortControllerRef.current.signal
      });

      console.log('Accept API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to accept proposal');
      }

      const updatedProposal = await response.json();
      setProposal(updatedProposal.proposal);
      setSnackbar({ open: true, message: 'Proposal accepted successfully!', severity: 'success' });
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Accept request aborted');
        return;
      }
      console.error('Error accepting proposal:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setAcceptLoading(false);
        setAcceptingProposal(false);
      }
    }
  };

  // Check if customer has signed and proposal is accepted
  const isCustomerSigned = !!proposal?.customerSignature;
  const isProposalAccepted = proposal?.status === 'accepted';

  // Debug logging
  console.log('Render - isCustomerSigned:', isCustomerSigned);
  console.log('Render - isProposalAccepted:', isProposalAccepted);
  console.log('Render - proposal status:', proposal?.status);
  console.log('Render - customerSignature value:', proposal?.customerSignature);
  
  // Force re-render when signature changes
  useEffect(() => {
    if (proposal?.customerSignature) {
      console.log('Customer signature detected, enabling accept button');
    }
  }, [proposal?.customerSignature]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!proposal) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Proposal not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        py: 4,
        px: { xs: 2, sm: 3 },
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: `${brandColors.accent}`,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}
    >
      {/* Print styles */}
      <style>{printStyles}</style>
      
      {/* Print and Download buttons */}
      <Box className="no-print" sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={pdfLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={pdfLoading}
          sx={{ 
            borderColor: brandColors.primary,
            color: brandColors.primary,
            '&:hover': {
              borderColor: `${brandColors.primary}dd`,
              backgroundColor: `${brandColors.primary}11`
            }
          }}
        >
          {pdfLoading ? 'Generating...' : 'Download PDF'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ 
            borderColor: brandColors.primary,
            color: brandColors.primary,
            '&:hover': {
              borderColor: `${brandColors.primary}dd`,
              backgroundColor: `${brandColors.primary}11`
            }
          }}
        >
          Print
        </Button>
      </Box>
      
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: brandColors.light,
          width: '100%',
          minHeight: '29.7cm',
          position: 'relative',
          margin: '0 auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.05)',
          borderRadius: 2
        }}
      >
        {/* Logo Section */}
        <Box className="logo-title-section no-page-break" sx={{ mb: 4, mt: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              {companySettings?.companyLogo ? (
                <Box 
                  component="img" 
                  src={getLogoUrl(companySettings.companyLogo)} 
                  alt={companySettings.companyName || 'Company Logo'}
                  sx={{ 
                    maxHeight: 80, 
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                  className="company-logo"
                  onError={(e) => {
                    console.error('Error loading logo:', e);
                    e.target.src = ''; // Clear the src to prevent further error attempts
                    e.target.alt = companySettings.companyName || 'Company';
                    e.target.style.fontWeight = 'bold';
                    e.target.style.fontSize = '1.5rem';
                    e.target.style.color = brandColors.primary;
                  }}
                />
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 700, color: brandColors.primary }}>
                  {companySettings?.companyName || 'Company Name'}
                </Typography>
              )}
            </Grid>
            
            {/* Company Contact Information - Centered below logo */}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                {companySettings?.companyAddress && (
                  <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                    {companySettings.companyAddress}
                  </Typography>
                )}
                {(companySettings?.companyPhone || companySettings?.companyEmail) && (
                  <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                    {companySettings?.companyPhone && companySettings.companyPhone}
                    {companySettings?.companyPhone && companySettings?.companyEmail && ' • '}
                    {companySettings?.companyEmail && companySettings.companyEmail}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        {/* Header and Customer Info Boxes - Side by Side */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Customer Information Box - Left Side */}
          <Grid item xs={12} md={6}>
            <Box className="customer-info no-page-break" sx={{ 
              height: '100%',
              p: 3, 
              backgroundColor: `${brandColors.accent}50`,
              borderRadius: 2,
              border: `1px solid ${brandColors.primary}15`,
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography 
                    variant="h6" 
                    component="h2" 
                    gutterBottom
                    sx={{ 
                      color: brandColors.secondary,
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      mb: 2
                    }}
                  >
                    Prepared For:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {proposal.customer?.companyName && (
                      <Typography variant="body1" sx={{ fontWeight: 600, color: brandColors.secondary }}>
                        {proposal.customer.companyName}
                      </Typography>
                    )}
                    {proposal.customer?.contactPerson && (
                      <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                        Attn: {proposal.customer.contactPerson}
                      </Typography>
                    )}
                    {proposal.customer?.email && (
                      <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                        {proposal.customer.email}
                      </Typography>
                    )}
                    {proposal.customer?.phone && (
                      <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                        {proposal.customer.phone}
                      </Typography>
                    )}
                    {proposal.customer?.address && (
                      <Typography variant="body2" sx={{ color: brandColors.secondary, mt: 1 }}>
                        {proposal.customer.address}
                      </Typography>
                    )}
                    {(proposal.customer?.city || proposal.customer?.state || proposal.customer?.zipCode) && (
                      <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                        {proposal.customer.city}{proposal.customer.city && proposal.customer.state ? ', ' : ''}
                        {proposal.customer.state} {proposal.customer.zipCode}
                      </Typography>
                    )}
                    {proposal.customer?.country && (
                      <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                        {proposal.customer.country}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                
                {proposal.validUntil && (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: brandColors.light,
                      borderRadius: 1,
                      border: `1px dashed ${brandColors.primary}40`,
                      mt: { xs: 2, sm: 0 }
                    }}>
                      <Typography variant="body2" sx={{ color: brandColors.secondary, fontWeight: 500 }}>
                        Valid Until:
                      </Typography>
                      <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600 }}>
                        {format(new Date(proposal.validUntil), 'PPP')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
          
          {/* Professional Header Box - Right Side */}
          <Grid item xs={12} md={6}>
            <Box className="proposal-header no-page-break" sx={{ 
              height: '100%',
              p: 3,
              backgroundColor: `${brandColors.accent}50`,
              borderRadius: 2,
              border: `1px solid ${brandColors.primary}15`,
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
              color: brandColors.secondary
            }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography 
                    variant="h6" 
                    component="h2" 
                    gutterBottom
                    sx={{ 
                      color: brandColors.secondary,
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      mb: 2
                    }}
                  >
                    Prepared By:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {proposal.creator && companySettings?.companyName ? (
                      <Typography variant="body1" sx={{ fontWeight: 600, color: brandColors.secondary }}>
                        {proposal.creator.firstName} {proposal.creator.lastName} - {companySettings.companyName}
                      </Typography>
                    ) : (
                      <>
                        {proposal.creator && (
                          <Typography variant="body1" sx={{ fontWeight: 600, color: brandColors.secondary }}>
                            {proposal.creator.firstName} {proposal.creator.lastName}
                          </Typography>
                        )}
                        {companySettings?.companyName && (
                          <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                            {companySettings.companyName}
                          </Typography>
                        )}
                      </>
                    )}
                    {/* Address, phone and email moved to below the logo */}
                    
                    {/* Valid until, Created by, Created on information */}
                    <Box sx={{ mt: 0, pt: 0, borderTop: `1px solid ${brandColors.primary}15` }}>
                      <Typography variant="body2" sx={{ color: brandColors.secondary, fontWeight: 500}}>
                          <span style={{ color: '#454546', fontWeight: 600 }}>Created on:</span> {format(new Date(proposal.createdAt), 'PPP')}
                      </Typography>                      
                      {proposal.createdAt && (
                      <Typography variant="body2" sx={{ color: brandColors.secondary, fontWeight: 500, mt: 1 }}>
                      <span style={{ color: '#454546', fontWeight: 600 }}>Valid until:</span> {proposal.validUntil ? format(new Date(proposal.validUntil), 'PPP') : 'Not specified'}
                     </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
        
        {/* Title Section - Moved below the two boxes */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              //color: brandColors.primary,
              color: '#454546',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.25rem' }
            }}
          >
            {proposal.title}
          </Typography>
        </Box>
        
        {/* Proposal Content */}
        <Box className="proposal-content" sx={{ 
          mb: 4,
          p: 3,
          backgroundColor: brandColors.light,
          borderRadius: 2,
          '& p': {
            lineHeight: 1.6,
            fontSize: '1rem',
            color: '#454546' // Changed from brandColors.secondary to a dark color
          },
          '& li': {
            lineHeight: 1.6,
            fontSize: '1rem',
            color: '#454546' // Changed from brandColors.secondary to a dark color
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: '#454546',
            fontWeight: 600,
            marginTop: '1.5em',
            marginBottom: '0.5em'
          },
          '& h1': { fontSize: '1.8rem' },
          '& h2': { fontSize: '1.6rem' },
          '& h3': { fontSize: '1.4rem' },
          '& h4': { fontSize: '1.2rem' },
          '& h5': { fontSize: '1.1rem' },
          '& h6': { fontSize: '1rem' },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '1.5em',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          },
          '& th': {
            backgroundColor: `${brandColors.primary}15`,
            color: '#454546', // Changed from brandColors.secondary to a dark color
            fontWeight: 600,
            padding: '12px',
            textAlign: 'left',
            borderBottom: `2px solid ${brandColors.primary}25`
          },
          '& td': {
            padding: '12px',
            borderBottom: `1px solid ${brandColors.primary}15`,
            color: '#454546' // Changed from brandColors.secondary to a dark color
          },
          '& tr:nth-of-type(even)': {
            backgroundColor: `${brandColors.accent}50`
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
            margin: '1em 0'
          },
          '& blockquote': {
            borderLeft: `4px solid ${brandColors.primary}40`,
            padding: '0.5em 1em',
            margin: '1em 0',
            backgroundColor: `${brandColors.accent}50`,
            borderRadius: '0 4px 4px 0',
            fontStyle: 'italic'
          },
          // Add specific paragraph spacing control
          '& p': {
            margin: 0,
            padding: 0
          },
          '& p + p': {
            marginTop: '0.5em'
          }
        }}>
          <div className="proposal-content-display" dangerouslySetInnerHTML={{ __html: proposal.content }} />
        </Box>
        
        <Divider sx={{ 
          my: 3, 
          borderColor: `${brandColors.primary}50`
        }} />

        {/* Company Signature Section */}
        {proposal.companySignature && (
          <>
            <Box mb={4} className="signature-section no-page-break">
              <Typography 
                variant="h5" 
                component="h3" 
                gutterBottom
                sx={{ 
                  color: brandColors.secondary,
                  fontWeight: 600,
                  borderLeft: `4px solid ${brandColors.primary}`,
                  paddingLeft: 2,
                  mb: 3
                }}
              >
                Our Commitment
              </Typography>
              
              <Card sx={{ 
                backgroundColor: `${brandColors.accent}`, 
                border: `1px solid ${brandColors.primary}25`,
                boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ color: brandColors.primary, mr: 1 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: brandColors.primary,
                        fontWeight: 600
                      }}
                    >
                      Company Signature
                    </Typography>
                  </Box>
                  
                  <Box sx={{
                    border: `1px solid ${brandColors.primary}25`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: brandColors.light,
                    mb: 2,
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)'
                  }}>
                    {(() => {
                      try {
                        const signatureData = JSON.parse(proposal.companySignature);
                        return (
                          <>
                            <Typography variant="h4" sx={{
                              fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Print", cursive',
                              color: brandColors.primary,
                              fontWeight: 400,
                              fontSize: '2rem',
                              fontStyle: 'italic',
                              mb: 1
                            }}>
                              {signatureData.signerName || signatureData.signature}
                            </Typography>
                            <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                              Signed on {format(new Date(signatureData.signedAt), 'PPP p')}
                            </Typography>
                          </>
                        );
                      } catch (error) {
                        console.error('Error parsing company signature:', error);
                        return (
                          <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                            Company signature available
                          </Typography>
                        );
                      }
                    })()}
                  </Box>
                </CardContent>
              </Card>
            </Box>
            
            <Divider sx={{ 
              my: 3, 
              borderColor: `${brandColors.primary}50`
            }} />
          </>
        )}

        {/* Customer Signature Section */}
        {isCustomerSigned && (
          <>
            <Box mb={4} className="signature-section no-page-break">
              <Typography 
                variant="h5" 
                component="h3" 
                gutterBottom
                sx={{ 
                  color: brandColors.secondary,
                  fontWeight: 600,
                  borderLeft: `4px solid ${brandColors.primary}`,
                  paddingLeft: 2,
                  mb: 3
                }}
              >
                Customer Approval
              </Typography>
              
              <Card sx={{ 
                backgroundColor: `${brandColors.accent}`, 
                border: `1px solid ${brandColors.primary}25`,
                boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ color: brandColors.primary, mr: 1 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: brandColors.primary,
                        fontWeight: 600
                      }}
                    >
                      Customer Signature
                    </Typography>
                  </Box>
                  
                  <Box sx={{
                    border: `1px solid ${brandColors.primary}25`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: brandColors.light,
                    mb: 2,
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)'
                  }}>
                    {(() => {
                      try {
                        const signatureData = JSON.parse(proposal.customerSignature);
                        return (
                          <>
                            <Typography variant="h4" sx={{
                              fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Print", cursive',
                              color: brandColors.primary,
                              fontWeight: 400,
                              fontSize: '2rem',
                              fontStyle: 'italic',
                              mb: 1
                            }}>
                              {signatureData.signerName || signatureData.signature}
                            </Typography>
                            <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                              Signed on {format(new Date(signatureData.signedAt), 'PPP p')}
                            </Typography>
                          </>
                        );
                      } catch (error) {
                        console.error('Error parsing customer signature:', error);
                        return (
                          <Typography variant="body2" sx={{ color: brandColors.secondary }}>
                            Customer signature available
                          </Typography>
                        );
                      }
                    })()}
                  </Box>
                </CardContent>
              </Card>
            </Box>
            
            <Divider sx={{ 
              my: 3, 
              borderColor: `${brandColors.primary}50`
            }} />
          </>
        )}

        {/* Customer Acceptance Section */}
        {!isCustomerSigned && (
          <Box mb={4} className="signature-section no-page-break">
            <Typography 
              variant="h5" 
              component="h3" 
              gutterBottom
              sx={{ 
                color: brandColors.secondary,
                fontWeight: 600,
                borderLeft: `4px solid ${brandColors.primary}`,
                paddingLeft: 2,
                mb: 3
              }}
            >
              Accept Proposal
            </Typography>
            
            <Card sx={{ 
              backgroundColor: `${brandColors.accent}`, 
              border: `1px solid ${brandColors.primary}25`,
              boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EditIcon sx={{ color: brandColors.primary, mr: 1 }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: brandColors.primary,
                      fontWeight: 600
                    }}
                  >
                    Digital Signature
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 3, color: brandColors.secondary }}>
                  By signing below, you agree to the terms and conditions outlined in this proposal.
                </Typography>
                
                <Box sx={{
                  border: `1px solid ${brandColors.primary}25`,
                  borderRadius: 1,
                  p: 3,
                  backgroundColor: brandColors.light,
                  mb: 3,
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)'
                }}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    variant="outlined"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    sx={{ 
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: brandColors.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: brandColors.primary,
                      }
                    }}
                  />
                  
                  {!signatureData ? (
                    <Button
                      variant="contained"
                      sx={{ 
                        mb: 2, 
                        backgroundColor: brandColors.primary,
                        '&:hover': {
                          backgroundColor: `${brandColors.primary}dd`
                        }
                      }}
                      startIcon={<CreateIcon />}
                      onClick={generateSignature}
                      disabled={!signerName.trim()}
                    >
                      Generate Signature
                    </Button>
                  ) : (
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="h4" sx={{
                        fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Print", cursive',
                        color: brandColors.primary,
                        fontWeight: 400,
                        fontSize: '2rem',
                        fontStyle: 'italic'
                      }}>
                        {signerName}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setSignatureData(null)}
                        sx={{ 
                          mt: 1,
                          color: brandColors.secondary
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  )}
                  
                  <Typography variant="body2" sx={{ mt: 2, color: `${brandColors.secondary}99` }}>
                    This digital signature will be recorded along with your IP address and timestamp.
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={acceptingProposal}
                  onClick={handleAcceptProposal}
                  sx={{ 
                    mt: 2,
                    backgroundColor: brandColors.primary,
                    '&:hover': {
                      backgroundColor: `${brandColors.primary}dd`
                    },
                    '&.Mui-disabled': {
                      backgroundColor: `${brandColors.primary}55`
                    }
                  }}
                >
                  {acceptingProposal ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Accept Proposal'
                  )}
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Footer Section */}
        <Box className="footer-section no-page-break" sx={{ 
          mt: 5, 
          pt: 3, 
          borderTop: `2px solid ${brandColors.primary}25`,
          backgroundColor: `${brandColors.accent}50`,
          borderRadius: 1,
          p: 2
        }}>
          {companySettings?.companyName && (
            <Box sx={{ 
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ color: `${brandColors.secondary}99`, fontWeight: 500 }}>
                © {new Date().getFullYear()} {companySettings.companyName} - All Rights Reserved
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: `${brandColors.secondary}80` }}>
                This document is confidential and contains proprietary information.
              </Typography>
            </Box>
          )}
        </Box>


      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default PublicProposal;
